import React, { useCallback } from "react";
import {
  Accordion, AccordionSummary, AccordionDetails, Box, Button,
  CircularProgress, TextField, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { useTranslation } from "react-i18next";
import { SETTING_TABLE_NAME } from "../../../constants/settingsTableName";
import { useMsal } from "Auth/msal-context";
import { useKeyboard } from "../../../hooks/KeyboardContext";
import useSettings from "../../../hooks/useSettings";

const transformResponse = (res) => res.jsonData;

const AnalogSignalSettings = () => {
  const { t } = useTranslation("common");
  const { isAuthorized, isEdge } = useMsal();
  const { registerInput } = useKeyboard();

  const { formState, setFormState, loading, error, fetchSettings, saveSettings } =
    useSettings(SETTING_TABLE_NAME.ANALOG_SIGNAL_SETTINGS, transformResponse);

  const updateSignalField = useCallback((index, field, value) => {
    setFormState((prev) => {
      if (!Array.isArray(prev)) return prev;
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  }, [setFormState]);

  const handleSave = () => {
    if (!formState) return;
    saveSettings(formState);
  };

  const mAFields = ["0mA", "4mA", "20mA"];

  return (
    <Accordion sx={{ mb: 2 }} disableGutters onChange={(e, expanded) => { if (expanded) fetchSettings(); }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ bgcolor: "primary.main", color: "common.white" }}>
        <Typography sx={{ fontWeight: "bold", fontSize: "1.5rem" }}>{t("Analog Signal Settings")}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ bgcolor: "background.light", p: 2 }}>
        {loading && <CircularProgress size={24} />}
        {error && (
          <Typography sx={{ textAlign: "center", color: "secondary.main" }}>{error}</Typography>
        )}
        {!loading && Array.isArray(formState) && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Header row */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 0.5 }}>
              <Typography variant="h6" sx={{ width: 100 }}>{t("Channel")}</Typography>
              <Typography variant="h6" sx={{ width: 100 }}>{t("Name")}</Typography>
              {mAFields.map((f) => (
                <Typography key={f} variant="h6" sx={{ width: 100, textAlign: "center" }}>{f}</Typography>
              ))}
            </Box>
            {formState.map((signal, idx) => (
              <Box key={idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2" sx={{ width: 100, flexShrink: 0 }}>{signal.channel}</Typography>
                <Typography variant="body2" sx={{ width: 100, flexShrink: 0 }}>{signal.name}</Typography>
                {mAFields.map((field) => (
                  <TextField
                    key={field}
                    size="small"
                    value={signal[field] ?? ""}
                    onChange={(e) => updateSignalField(idx, field, e.target.value)}
                    onClick={(e) => registerInput(`${idx}|${field}`, e.target.value, (v) => updateSignalField(idx, field, v))}
                    inputProps={{ inputMode: "decimal" }}
                    sx={{ width: 100, textAlign: "center" }}
                  />
                ))}
              </Box>
            ))}
          </Box>
        )}
        {(isAuthorized || isEdge) && formState && (
          <Box
            sx={{
              display: "flex", justifyContent: "flex-end", mt: 2
            }}
          >
            < Button variant="contained" size="small" onClick={handleSave} > {t("Save")}</Button>
          </Box>
        )
        }
      </AccordionDetails >
    </Accordion >
  );
};

export default AnalogSignalSettings;